import React, { useMemo } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult
} from "react-beautiful-dnd";
import moment from "moment-timezone";
import { type ActiveEvent, type GameConfig } from "kadow-jetpack-model";
import { faBars, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import KC_Heading2 from "../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../../designsystem/KC_Button/KC_Button";
import DashedCard from "../../../../../components/DashedCard/DashedCard";
import { tt } from "../../../../../utils/i18n/i18n";
import { useEventsSelector } from "../../../../../redux/actions/wheel";
import { useJetpackConfigSelector } from "../../../../../redux/jetpackConfig";
import { CreateAnimationStep } from "../../../../../components/modal/ConfigAnimationModal/CreateAnimationModal";
import GameCard from "./GameCard";
import { useReorderableGameList } from "./useReorderableGameList";
import "../styles.scss";
import {
  buildEventItem,
  buildGameItem,
  getEventCardData,
  getGameThumbnail,
  orderGameListItems,
  type GameListItem,
  type GameListMode,
  type GameListSection
} from "./gameListTypes";

const DROPPABLE_PREFIX = "game-list-section-";

interface GameListViewProps {
  mode: GameListMode;
  wrap?: boolean;
  canUseGamesOfChance: boolean;
  canUsePerformanceGames: boolean;
  showCreateAnimationModal: (step: CreateAnimationStep) => Promise<void>;
}

export default function GameListView(props: GameListViewProps) {
  const events = useEventsSelector();
  const config = useJetpackConfigSelector();
  const nowTs = useMemo(() => moment().unix(), []);
  const {
    games,
    activeEvents,
    canDeleteGames,
    commitOrder,
    deleteGame,
    deleteEvent
  } = useReorderableGameList();

  const canAdd = props.canUseGamesOfChance || props.canUsePerformanceGames;

  const sections = useMemo<GameListSection[]>(() => {
    const toGameItems = (list: GameConfig[]): GameListItem[] => {
      return list.filter((g) => !!getGameThumbnail(g)).map(buildGameItem);
    };

    const toEventItems = (list: ActiveEvent[]): GameListItem[] => {
      return list
        .filter((ae) => !!getEventCardData(ae, events, config, nowTs))
        .map((ae) => buildEventItem(ae));
    };

    if (props.mode === "twoSections") {
      const scheduledEvents = activeEvents.filter((ae) => !!ae.s);
      const chosenEvents = activeEvents.filter((ae) => !ae.s);
      return [
        {
          key: "scheduled",
          title: tt("scheduled_games_title"),
          description: tt("scheduled_games_description"),
          items: orderGameListItems(toEventItems(scheduledEvents))
        },
        {
          key: "chosen",
          title: tt("your_chosen_games_title"),
          description: tt("your_chosen_games_description"),
          items: orderGameListItems([
            ...toGameItems(games),
            ...toEventItems(chosenEvents)
          ])
        }
      ];
    }

    return [
      {
        key: "all",
        items: orderGameListItems([
          ...toGameItems(games),
          ...toEventItems(activeEvents)
        ])
      }
    ];
  }, [props.mode, games, activeEvents, events, config, nowTs]);

  const isEmpty = sections.every((section) => !section.items.length);

  const showDashedAddButton = isEmpty && canAdd;

  const addNew = () => {
    void props.showCreateAnimationModal(CreateAnimationStep.EventConfig);
  };

  const onDragEnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.source.droppableId !== result.destination.droppableId ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const sectionIdx = Number(
      result.source.droppableId.replace(DROPPABLE_PREFIX, "")
    );
    const reorderedItems = [...sections[sectionIdx].items];
    const [moved] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, moved);

    const reorderedSections = sections.map((section, idx) =>
      idx === sectionIdx ? { ...section, items: reorderedItems } : section
    );

    commitOrder(reorderedSections.flatMap((section) => section.items));
  };

  const renderAddButton = () => {
    if (!canAdd) {
      return null;
    }

    return (
      <>
        <KC_Button
          id="add-ephemeral-game"
          size={KC_ButtonSize.Large}
          showIconOnTheLeft={true}
          icon={faPlus}
          onClick={addNew}
          isPrimary
          text={tt("add_button")}
          className="d-none d-sm-flex"
          extraStyle={{ width: "unset !important" }}
        />
        <KC_Button
          id="add-ephemeral-game-mobile"
          size={KC_ButtonSize.Medium}
          showIconOnTheLeft={true}
          icon={faPlus}
          onClick={addNew}
          isPrimary
          text={tt("add_button")}
          className="d-flex d-sm-none"
          extraStyle={{ width: "unset !important" }}
        />
      </>
    );
  };

  const renderItem = (item: GameListItem, idx: number) => {
    return (
      <Draggable key={item.key} draggableId={item.key} index={idx}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={props.wrap ? "game-list-wrap-item" : undefined}
          >
            <div className="d-flex align-items-center st-gap">
              <div
                {...provided.dragHandleProps}
                className="game-list-drag-handle"
              >
                <FontAwesomeIcon icon={faBars} />
              </div>
              <div className="flex-grow-1 w-100">
                <GameCard
                  item={item}
                  showScheduledExplanation={props.mode !== "twoSections"}
                  canDeleteGames={canDeleteGames}
                  onDeleteGame={() =>
                    item.type === "game" && deleteGame(item.game)
                  }
                  onDeleteEvent={() =>
                    item.type === "event" && deleteEvent(item.ae)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const renderSection = (section: GameListSection, sectionIdx: number) => {
    if (!section.title && !section.items.length) {
      return null;
    }

    return (
      <div key={section.key} className="d-flex flex-column st-gap">
        {section.title && (
          <div className="d-flex flex-column">
            <KC_Heading2 text={section.title} />
            {section.description && (
              <KC_UIText weight="reg" text={section.description} />
            )}
          </div>
        )}
        <Droppable
          droppableId={`${DROPPABLE_PREFIX}${sectionIdx}`}
          direction={props.wrap ? "horizontal" : "vertical"}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={
                props.wrap
                  ? "game-list-wrap st-gap"
                  : "d-flex flex-column st-gap"
              }
            >
              {section.items.map((item, idx) => renderItem(item, idx))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  return (
    <div
      id="game-and-events-section"
      className="px-4 pt-5 d-flex flex-column st-gap"
      style={{ marginBottom: 40 }}
    >
      <div className="d-flex flex-row align-items-center w-100 gap-sm">
        <KC_Heading2 text={tt("your_animation")} className="flex-grow-1" />
        {!showDashedAddButton && renderAddButton()}
      </div>

      {showDashedAddButton && (
        <DashedCard>
          <KC_Button
            type={KC_ButtonType.Button}
            size={KC_ButtonSize.Large}
            isPrimary={true}
            showIconOnTheLeft={true}
            icon={faPlus}
            onClick={addNew}
            text={tt("create_first_animation")}
            className="d-flex"
          />
        </DashedCard>
      )}

      {!isEmpty && (
        <DragDropContext onDragEnd={onDragEnd}>
          {sections.map(renderSection)}
        </DragDropContext>
      )}
    </div>
  );
}
